import { Trans, useTranslation } from "react-i18next"
import { Timestamp } from "firebase/firestore"
import Callout from "./callout"
import Button from "../inputs/button"
import { ReactComponent as RestoreIcon } from "assets/icons/restore_from_trash.svg"
import ProcessingButton from "../inputs/processingButton"
import DateTime from "@/components/text/DateTime"
import { APP_CONFIG } from "@/constants/constants"

interface DeletedDocCalloutProps {
    documentType: string // e.g., "storage", "product", "customer", "category"
    deletionType: "hard" | "soft" // "hard" means permanent deletion scheduled, "soft" means indefinite
    action: () => void | Promise<void>
    isRestoring?: boolean
    deletedAt?: Timestamp | null
}

const getPurgeDate = (deletedAt: Timestamp): Date => {
    const deletedDate = deletedAt.toDate()
    return new Date(
        deletedDate.getTime() + APP_CONFIG.PERMANENT_DELETION_GRACE_DAYS * 24 * 60 * 60 * 1000
    )
}

const DeletedDocCallout = ({
    documentType,
    deletionType,
    action,
    isRestoring = false,
    deletedAt
}: DeletedDocCalloutProps) => {
    const { t } = useTranslation()

    const getDescription = () => {
        if (deletionType === "soft") {
            return t("DeletedDocCalloutDescriptionSoft", { documentType })
        }

        if (deletedAt) {
            return (
                <Trans
                    i18nKey="DeletedDocCalloutDescriptionHard"
                    values={{ documentType }}
                    components={{ datetime: <DateTime timestamp={getPurgeDate(deletedAt)} /> }}
                />
            )
        }

        return t("DeletedDocCalloutDescriptionHardNoDate", { documentType })
    }

    return (
        <Callout type="error" title={t("DeletedDocCalloutTitle", { documentType })}>
            <p>{getDescription()}</p>
            {isRestoring ?
            <ProcessingButton design="primary" /> :
            <Button wideOnMobile
                design="primary"
                onClick={action}
                disabled={isRestoring}
            >
                <RestoreIcon />
                <span>{isRestoring ? t("restoringItem") : t("DeletedDocCalloutRestoreButton", { documentType })}</span>
            </Button>
            }
        </Callout>
    )
}

export default DeletedDocCallout
